import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { IndianPokerObserverView } from "./GameScreen";
import { INDIAN_POKER_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import type { IndianPokerReplaySnapshot } from "./replay-runner";
import { buildIndianPokerReplaySnapshots } from "./replay-runner";

interface ReplayScreenProps {
  readonly scenario: string;
}

function actorName(
  snapshot: IndianPokerReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  return namesByPlayerId[snapshot.event.userId] ?? snapshot.event.userId;
}

function eventSummary(snapshot: IndianPokerReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

function clampStepIndex(
  index: number,
  snapshots: readonly IndianPokerReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly IndianPokerReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly IndianPokerReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = INDIAN_POKER_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildIndianPokerReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const maxIndex = snapshots.length - 1;

  return (
    <div
      className="min-h-screen bg-surface-primary"
      data-testid="indian-poker-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="indian-poker-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="indian-poker-replay-event-summary"
            >
              {eventSummary(current)} · {actorName(current, namesByPlayerId)}
            </p>
            <p className="text-text-secondary text-xs">
              {current.viewerState.round.phase} · {current.viewerState.status}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="indian-poker-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="indian-poker-replay-step-slider"
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="indian-poker-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="indian-poker-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <IndianPokerObserverView
        viewerState={current.viewerState}
        getPlayerName={(playerId) => namesByPlayerId[playerId] ?? playerId}
        leaveLabel="닫기"
        onLeave={() => navigateTo("/")}
      />
    </div>
  );
}
